import { Injectable } from '@angular/core';
import type * as mapboxgl from 'mapbox-gl';

// Iconos SDF para los pins temáticos. SDF permite tintar con `icon-color`
// data-driven sin generar un PNG por estado.
//   - PIN_TEMATICO_ID: gota / teardrop SÓLIDA (sin hueco). El usuario consume
//     este icono como capa coloreada de fondo.
//   - PIN_DISCO_ID: disco blanco más chico ubicado en la "burbuja" del pin.
//     Va en una segunda capa stackeada — sobre el teardrop, debajo del texto.
// Ambos canvas tienen la misma dimensión 64×80; con icon-anchor 'bottom' y
// el disco dibujado a la altura de la burbuja del pin, ambos se alinean
// automáticamente al renderearse.
const PIN_TEMATICO_ID = 'pin-tematico';
const PIN_DISCO_ID = 'pin-tematico-disco';
const PIN_W = 64;
const PIN_H = 80;
// Centro vertical de la burbuja en coordenadas del canvas (top = 0).
// Lo exportamos para que el componente que arma las layers calcule el
// text-offset y sincronice texto + disco.
export const PIN_BULB_CENTER_Y = 30;
export const PIN_HEIGHT = PIN_H;

const CLUSTER_BASE = '/assets/images/marker-cluster/originales-2';

@Injectable({ providedIn: 'root' })
export class MapaIconosService {
  // Red de seguridad para `styleimagemissing`: los PNGs legacy
  // (pin-<division>-<estado> y cluster-<n>) ya no se precargan — eran 64
  // requests en el critical path de cada style.load y ninguna capa actual
  // los usa. Si una capa (actual o futura) referencia uno, Mapbox dispara
  // styleimagemissing, lo cargamos acá y el mapa re-renderea solo al
  // completarse el addImage.
  public cargarImagenFaltante(map: mapboxgl.Map, id: string): void {
    if (id === PIN_TEMATICO_ID || id === PIN_DISCO_ID) {
      this.registrarPinTematico(map);
      return;
    }
    if (id.startsWith('cluster-')) {
      const n = id.slice('cluster-'.length);
      void this.registrarImagen(map, id, `${CLUSTER_BASE}/m${n}.png`);
      return;
    }
    if (id.startsWith('pin-')) {
      const resto = id.slice('pin-'.length);
      const sep = resto.lastIndexOf('-');
      if (sep <= 0) return;
      const division = resto.slice(0, sep);
      const estado = resto.slice(sep + 1);
      void this.registrarPin(map, division, estado);
    }
  }

  // Registra (o re-registra tras setStyle) los iconos del pin temático.
  // Sincrónico: los canvas se generan en memoria, sin red.
  // - Pin (teardrop) → SDF, así Mapbox lo tinta con icon-color por estado.
  // - Disco blanco → bitmap normal (NO SDF). El SDF de Mapbox espera un
  //   verdadero signed distance field; un alpha binario se distorsiona al
  //   escalar. Como el disco es siempre blanco, no necesitamos tinte.
  public registrarPinTematico(map: mapboxgl.Map): void {
    if (!map.hasImage(PIN_TEMATICO_ID)) {
      map.addImage(PIN_TEMATICO_ID, this.generarPinSolidoSDF(), {
        sdf: true,
        pixelRatio: 2,
      });
    }
    if (!map.hasImage(PIN_DISCO_ID)) {
      map.addImage(PIN_DISCO_ID, this.generarPinDiscoBitmap(), {
        sdf: false,
        pixelRatio: 2,
      });
    }
  }

  // Teardrop sólido (sin hueco). Se tinta con icon-color en runtime.
  // Canvas a 2× resolución para que el SDF tenga más datos al re-escalar.
  private generarPinSolidoSDF(): ImageData {
    const SCALE = 2;
    const w = PIN_W * SCALE;
    const h = PIN_H * SCALE;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(SCALE, SCALE);

    ctx.fillStyle = '#ffffff';
    const cx = PIN_W / 2;
    const top = 2;
    const bottom = PIN_H - 1;
    const sideRadius = PIN_W / 2 - 2;

    ctx.beginPath();
    ctx.moveTo(cx, bottom);
    ctx.bezierCurveTo(
      cx - sideRadius * 1.4,
      bottom - PIN_H * 0.5,
      cx - sideRadius,
      top,
      cx,
      top,
    );
    ctx.bezierCurveTo(
      cx + sideRadius,
      top,
      cx + sideRadius * 1.4,
      bottom - PIN_H * 0.5,
      cx,
      bottom,
    );
    ctx.closePath();
    ctx.fill();

    return ctx.getImageData(0, 0, w, h);
  }

  // Disco blanco como bitmap normal (alpha + RGB blanco). No es SDF.
  // Canvas mismo tamaño que el pin para que con icon-anchor 'bottom' caiga
  // exactamente sobre la burbuja del pin.
  private generarPinDiscoBitmap(): ImageData {
    const SCALE = 2;
    const w = PIN_W * SCALE;
    const h = PIN_H * SCALE;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(SCALE, SCALE);

    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(PIN_W / 2, PIN_BULB_CENTER_Y, PIN_W * 0.3, 0, Math.PI * 2);
    ctx.fill();

    return ctx.getImageData(0, 0, w, h);
  }

  public async registrarPin(
    map: mapboxgl.Map,
    division: string,
    estado: string,
  ): Promise<void> {
    const id = `pin-${division}-${estado}`;
    const url = `/assets/pins/${division}/${estado}.png`;
    return this.registrarImagen(map, id, url);
  }

  public registrarImagen(
    map: mapboxgl.Map,
    id: string,
    url: string,
  ): Promise<void> {
    if (map.hasImage(id)) return Promise.resolve();
    return new Promise<void>((resolve) => {
      map.loadImage(url, (error, image) => {
        if (error || !image) {
          resolve();
          return;
        }
        if (!map.hasImage(id)) {
          map.addImage(id, image);
        }
        resolve();
      });
    });
  }
}
